import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useFeatureValue } from '@growthbook/growthbook-react';
import {
    PARTNERSHIP_HEIGHT_UPDATE_COMMAND,
    PartnershipSlotConfig,
} from '@gumtree/shared/src/types/advertising/partnership';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { getTargetOrigin, validateOrigin } from '@gumtree/shared/src/util/message-helpers';
import getSlotConfig, { AdSlotConfig } from './slot-config';

const PartnershipAdSlot = forwardRef<HTMLIFrameElement, PartnershipSlotConfig>((props, ref) => {
    const iframeEl = useRef<HTMLIFrameElement>(null);
    const showCarMoneyMonthlyPrice =
        useFeatureValue(GrowthBookFeature.CARMONEY_MONTHLY_PRICE, 'A') === 'B';

    const [state, setState] = useState<State>(
        () => getSlotConfig({ ...props, showCarMoneyMonthlyPrice })!
    );
    const { slotId, width, style } = props;

    // Forward the internal iframe ref to the outside world
    useImperativeHandle(ref, () => iframeEl.current!, []);

    useEffect(() => {
        if (state) {
            const iframeId = iframeEl?.current?.id;
            const visibleSlots = ['partnerAdCompact1', 'partnerAdDefault1'];
            visibleSlots.forEach((slot) => {
                if (iframeId === slot) {
                    const slotType = slot.slice(slot.length - 8, slot.length - 1).toLowerCase();

                    const container: HTMLElement | null = document.querySelector(
                        `#partnership-${slotType}-container`
                    );
                    const containerSplit: HTMLElement | null = document.querySelector(
                        `#partnership-${slotType}-container-split`
                    );
                    if (containerSplit) {
                        containerSplit.style.display = slotType === 'compact' ? 'flex' : 'block';
                    }
                    if (container) {
                        container.style.display = slotType === 'compact' ? 'flex' : 'block';
                    }
                }
            });
        }

        if (state?.isResponsive) {
            const iFrameWidth = iframeEl?.current?.clientWidth;
            const srcWithIFrameWidth = `${state.src}&viewportWidth=${iFrameWidth}`;

            setTimeout(() => {
                setState((prev) => ({ ...prev, srcWithIFrameWidth }));
            }, 0);
        }

        if (state?.resizeCommand) {
            window.addEventListener('message', (event) => {
                if (!validateOrigin(window, event.origin)) {
                    return;
                }
                if (
                    typeof event.data === 'string' &&
                    state &&
                    typeof state.resizeCommand === 'string' &&
                    event.data.includes(state.resizeCommand) &&
                    getHostnameFromRegex(props.baseUrl) === getHostnameFromRegex(event.origin)
                ) {
                    const { command, height } = JSON.parse(event.data);
                    if (command === state.resizeCommand) {
                        setState((prev) => ({ ...prev, height }));
                    }
                }
            });
        }

        window.addEventListener('resize', () => {
            if (iframeEl?.current) {
                iframeEl?.current?.contentWindow?.postMessage(
                    PARTNERSHIP_HEIGHT_UPDATE_COMMAND,
                    getTargetOrigin(window)
                );
            }
        });

        if (state?.detectSeen && iframeEl.current) {
            const observer = new IntersectionObserver(
                (entries) => {
                    const sendMessage = () => {
                        iframeEl.current!.contentWindow?.postMessage(
                            JSON.stringify({ command: 'IFRAME_VISIBLE_IN_PARENT_DOCUMENT' }),
                            getTargetOrigin(window)
                        );
                    };
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            sendMessage();
                            // Send again in case iframe not listening yet (e.g. initially visible)
                            setTimeout(sendMessage, 2000);
                            observer.disconnect();
                        }
                    });
                },
                {
                    threshold: typeof state.detectSeen === 'number' ? state.detectSeen : 1,
                }
            );
            observer.observe(iframeEl.current);
        }
    }, []);

    return state ? (
        <iframe
            suppressHydrationWarning
            id={slotId}
            ref={iframeEl}
            width={width || state.width}
            height={state.height}
            src={state.isResponsive ? state.srcWithIFrameWidth : state.src}
            title={slotId}
            frameBorder="0"
            style={style}
            data-testid="partnership-iframe"
        />
    ) : (
        <></>
    );
});

type State = AdSlotConfig & { srcWithIFrameWidth?: string };

function getHostnameFromRegex(url): string | undefined {
    const matches = url.match(/^https?:\/\/([^\/?#]+)(?:[\/?#]|$)/i);
    // extract hostname (will be null if no match is found)
    return matches && matches[1];
}

PartnershipAdSlot.displayName = 'PartnershipAdSlot';

export default PartnershipAdSlot;
